import React, { useState } from 'react';
import { useShop } from '../../context/ShopContext';
import { 
  Layers, 
  Plus, 
  Trash2, 
  Edit3, 
  X, 
  Upload, 
  RotateCcw, 
  Check, 
  Search, 
  Sparkles, 
  Crown, 
  ExternalLink,
  Tag,
  Eye,
  AlertTriangle
} from 'lucide-react';

export default function AdminCategories() {
  const { 
    categories, 
    addCategory, 
    updateCategory, 
    deleteCategory, 
    resetCategoriesToDefault, 
    products, 
    setActiveCategory,
    setIsAdminMode,
    addToast 
  } = useShop();

  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirmCat, setDeleteConfirmCat] = useState(null);

  // Popular Pre-defined Jewellery Presets
  const popularPresets = [
    {
      id: 'gold-coins',
      name: 'Pure Gold Coins & Bars',
      subtitle: '24KT 999 Purity Hallmarked Bullion & Sovereigns',
      image: 'https://images.unsplash.com/photo-1610375461246-83df859d849d?auto=format&fit=crop&w=800&q=80',
      badge: '24K 999 PURE',
      isFeatured: true
    },
    {
      id: 'mangalsutra',
      name: 'Sacred Mangalsutras',
      subtitle: 'Auspicious Black Beads with 18K & 22K Gold Diamond Pendants',
      image: 'https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?auto=format&fit=crop&w=800&q=80',
      badge: 'SACRED HERITAGE',
      isFeatured: true
    },
    {
      id: 'mens-jewellery',
      name: "Men's Royal Collection",
      subtitle: 'Solid 22K Gold Chains, Kadas, Rings & Platinum Bands',
      image: 'https://images.unsplash.com/photo-1622398925373-3f91b1e275f5?auto=format&fit=crop&w=800&q=80',
      badge: "MEN'S LUXURY",
      isFeatured: true
    },
    {
      id: 'solitaires',
      name: 'Solitaire Masterpieces',
      subtitle: 'IGI Certified Cushion, Round & Emerald Cut Solitaires',
      image: 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?auto=format&fit=crop&w=800&q=80',
      badge: 'IGI CERTIFIED',
      isFeatured: true
    },
    {
      id: 'temple-antique',
      name: 'Temple Antique Heirlooms',
      subtitle: 'Devotional Nakshi, Goddess Lakshmi & Peacock Handcarvings',
      image: 'https://images.unsplash.com/photo-1611591475822-26c710ffc403?auto=format&fit=crop&w=800&q=80',
      badge: 'DEVOTIONAL NAKSHI',
      isFeatured: true
    },
    {
      id: 'kitchenware-appliances',
      name: 'Royal Cookware & Appliances',
      subtitle: 'Tri-Ply Stainless Steel, Brass Cookware & Luxury Kitchenware',
      image: '/images/products/triply_cookware_set.jpg',
      badge: 'CHEF CHOICE',
      isFeatured: true
    }
  ];

  const initialForm = {
    id: '',
    name: '',
    subtitle: '',
    image: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=800&q=80',
    badge: 'NEW COLLECTION',
    isFeatured: true
  };

  const [form, setForm] = useState(initialForm);

  const openAddModal = () => {
    setEditingId(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const openEditModal = (cat) => {
    setEditingId(cat.id);
    setForm({
      id: cat.id,
      name: cat.name || '',
      subtitle: cat.subtitle || '',
      image: cat.image || '',
      badge: cat.badge || '',
      isFeatured: cat.isFeatured !== false
    });
    setModalOpen(true);
  };

  const handleApplyPreset = (preset) => {
    const existing = categories.find(c => c.id === preset.id);
    if (existing) {
      setEditingId(existing.id);
      setForm({
        ...preset,
        ...existing
      });
      addToast(`Collection "${preset.name}" already exists. Opened in edit mode.`, 'info');
    } else {
      setEditingId(null);
      setForm({
        ...preset
      });
      addToast(`Preset "${preset.name}" loaded into form! Click "Create Category" to save.`, 'info');
    }
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      addToast('Please provide a category title', 'error');
      return;
    }

    if (editingId) {
      await updateCategory(editingId, form);
    } else {
      const rawSlug = form.id && form.id.trim() 
        ? form.id.trim() 
        : form.name.trim();
      const generatedSlug = rawSlug
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      
      const res = await addCategory({
        ...form,
        id: generatedSlug
      });
      if (res === false) return;
    }
    setModalOpen(false);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      addToast('Image size should be under 5MB', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      setForm((prev) => ({ ...prev, image: event.target?.result }));
      addToast('Cover image loaded!', 'info');
    };
    reader.readAsDataURL(file);
  };

  const handleViewLiveCategory = (catId) => {
    setActiveCategory(catId);
    setIsAdminMode(false);
    setTimeout(() => {
      const el = document.getElementById('catalog-section');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 150);
  };

  const filteredCategories = categories.filter((c) => {
    const term = searchTerm.toLowerCase();
    return (
      (c.name && c.name.toLowerCase().includes(term)) ||
      (c.id && c.id.toLowerCase().includes(term)) ||
      (c.subtitle && c.subtitle.toLowerCase().includes(term)) ||
      (c.badge && c.badge.toLowerCase().includes(term))
    );
  });

  return (
    <div style={{ maxWidth: '1180px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        marginBottom: '1.8rem'
      }}>
        <div>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem',
            color: 'var(--gold-400)',
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: '0.35rem'
          }}>
            <Layers size={14} /> Master Collection Architecture
          </div>
          <h2 style={{ fontSize: '1.65rem', fontWeight: 800, color: '#fff', margin: 0 }}>
            Jewellery & Kitchenware <span className="text-gold-gradient">Master Categories</span>
          </h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
            Dynamically create, customize, and feature collection categories across the homepage grid, filters, and catalog.
          </p>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.65rem' }}>
          <button
            onClick={openAddModal}
            className="btn btn-gold btn-sm"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.82rem', fontWeight: 700 }}
          >
            <Plus size={15} /> Add New Category
          </button>
        </div>
      </div>

      {/* Metrics Bar */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          background: '#13131e',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-md)',
          padding: '1.1rem 1.3rem'
        }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
            Total Categories
          </div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#fff', marginTop: '0.2rem' }}>
            {categories.length}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--gold-400)', marginTop: '0.2rem' }}>
            Live collections configured
          </div>
        </div>

        <div style={{
          background: '#13131e',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-md)',
          padding: '1.1rem 1.3rem'
        }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
            Homepage Grid Featured
          </div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#10b981', marginTop: '0.2rem' }}>
            {categories.filter(c => c.isFeatured !== false).length}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
            Visible in Explore Master Categories
          </div>
        </div>

        <div style={{
          background: '#13131e',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-md)',
          padding: '1.1rem 1.3rem'
        }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
            Total Products Classified
          </div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--gold-300)', marginTop: '0.2rem' }}>
            {products.length} Items
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
            Across all active vault inventories
          </div>
        </div>
      </div>

      {/* 1-Click Popular Presets Ribbon */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.08) 0%, rgba(19, 19, 30, 0.95) 100%)',
        border: '1px solid var(--border-gold)',
        borderRadius: 'var(--radius-md)',
        padding: '1rem 1.2rem',
        marginBottom: '1.8rem'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.45rem',
          color: 'var(--gold-300)',
          fontSize: '0.8rem',
          fontWeight: 700,
          marginBottom: '0.6rem'
        }}>
          <Crown size={15} /> 1-Click Quick Add High-Demand Jewellery Collections:
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {popularPresets.map((pre) => {
            const alreadyExists = categories.some(c => c.id === pre.id);
            return (
              <button
                key={pre.id}
                onClick={() => handleApplyPreset(pre)}
                style={{
                  background: alreadyExists ? 'rgba(255, 255, 255, 0.04)' : 'rgba(212, 175, 55, 0.12)',
                  border: alreadyExists ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid var(--gold-400)',
                  color: alreadyExists ? 'var(--text-muted)' : 'var(--gold-300)',
                  padding: '0.35rem 0.75rem',
                  borderRadius: 'var(--radius-full)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  transition: 'var(--transition)'
                }}
                title={alreadyExists ? 'Already added (Click to re-edit or re-add)' : 'Click to load preset'}
              >
                <Plus size={12} /> {pre.name}
                {alreadyExists && <span style={{ fontSize: '0.65rem', opacity: 0.7 }}>(Active)</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div style={{
        background: '#13131e',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: '0.9rem 1.2rem',
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        flexWrap: 'wrap'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.6rem',
          background: '#0d0d16',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-sm)',
          padding: '0.45rem 0.8rem',
          flex: 1,
          minWidth: '240px'
        }}>
          <Search size={15} color="var(--gold-400)" />
          <input
            type="text"
            placeholder="Search categories by name, slug, badge..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#fff',
              fontSize: '0.84rem',
              width: '100%'
            }}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
              <X size={14} />
            </button>
          )}
        </div>

        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          Showing <strong>{filteredCategories.length}</strong> of {categories.length} categories
        </div>
      </div>

      {/* Categories Visual Cards Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: '1.2rem'
      }}>
        {filteredCategories.map((cat) => {
          const productCount = products.filter(p => p.category === cat.id).length;
          return (
            <div
              key={cat.id}
              style={{
                background: '#13131e',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                transition: 'var(--transition)'
              }}
              className="category-admin-card"
            >
              {/* Cover Image Header */}
              <div style={{
                position: 'relative',
                height: '150px',
                background: '#0d0d16',
                overflow: 'hidden'
              }}>
                <img
                  src={cat.image || 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=800&q=80'}
                  alt={cat.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(to top, rgba(19, 19, 30, 0.95) 0%, rgba(19, 19, 30, 0.2) 65%)'
                }} />

                {/* Top Badges */}
                <div style={{
                  position: 'absolute',
                  top: '0.75rem',
                  left: '0.75rem',
                  right: '0.75rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  {cat.badge ? (
                    <span style={{
                      background: 'var(--gold-gradient)',
                      color: '#0a0a0f',
                      fontSize: '0.66rem',
                      fontWeight: 800,
                      padding: '0.2rem 0.5rem',
                      borderRadius: '4px',
                      letterSpacing: '0.04em',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.5)'
                    }}>
                      {cat.badge}
                    </span>
                  ) : <span />}

                  <span style={{
                    background: cat.isFeatured !== false ? 'rgba(16, 185, 129, 0.9)' : 'rgba(107, 114, 128, 0.85)',
                    color: '#fff',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    padding: '0.2rem 0.45rem',
                    borderRadius: '4px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}>
                    <Check size={11} /> {cat.isFeatured !== false ? 'Featured on Grid' : 'Hidden from Grid'}
                  </span>
                </div>

                {/* Category ID Slug Tag */}
                <div style={{
                  position: 'absolute',
                  bottom: '0.65rem',
                  left: '0.85rem',
                  background: 'rgba(0, 0, 0, 0.75)',
                  backdropFilter: 'blur(4px)',
                  color: 'var(--gold-300)',
                  fontSize: '0.72rem',
                  fontFamily: 'monospace',
                  padding: '0.15rem 0.5rem',
                  borderRadius: '4px',
                  border: '1px solid rgba(212, 175, 55, 0.3)'
                }}>
                  slug: <strong>{cat.id}</strong>
                </div>

                {/* Live Products Count Pill */}
                <div style={{
                  position: 'absolute',
                  bottom: '0.65rem',
                  right: '0.85rem',
                  background: 'rgba(0, 0, 0, 0.75)',
                  backdropFilter: 'blur(4px)',
                  color: '#fff',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  padding: '0.15rem 0.5rem',
                  borderRadius: '4px',
                  border: '1px solid rgba(255, 255, 255, 0.15)'
                }}>
                  {productCount} {productCount === 1 ? 'Product' : 'Products'}
                </div>
              </div>

              {/* Body Info */}
              <div style={{ padding: '1rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#fff', marginBottom: '0.35rem' }}>
                  {cat.name}
                </h3>
                <p style={{
                  fontSize: '0.8rem',
                  color: 'var(--text-muted)',
                  lineHeight: '1.4',
                  margin: 0,
                  flex: 1
                }}>
                  {cat.subtitle || 'No subtitle provided. Displays in product catalog & category exploration.'}
                </p>

                {/* Card Actions Footer */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: '1.2rem',
                  paddingTop: '0.85rem',
                  borderTop: '1px solid rgba(255, 255, 255, 0.06)'
                }}>
                  <button
                    onClick={() => handleViewLiveCategory(cat.id)}
                    className="btn-glass btn-sm"
                    style={{
                      fontSize: '0.74rem',
                      padding: '0.3rem 0.6rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      color: 'var(--gold-400)',
                      borderColor: 'rgba(212, 175, 55, 0.2)'
                    }}
                    title="View live category on storefront catalog"
                  >
                    <ExternalLink size={12} /> View Live Store
                  </button>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <button
                      onClick={() => openEditModal(cat)}
                      className="btn-glass btn-sm"
                      style={{
                        padding: '0.35rem 0.65rem',
                        fontSize: '0.75rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.3rem'
                      }}
                      title="Edit Category Details"
                    >
                      <Edit3 size={13} /> Edit
                    </button>

                    <button
                      onClick={() => setDeleteConfirmCat(cat)}
                      className="btn-glass btn-sm"
                      style={{
                        padding: '0.35rem 0.55rem',
                        color: '#f87171',
                        borderColor: 'rgba(248, 113, 113, 0.25)'
                      }}
                      title="Delete Category"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredCategories.length === 0 && (
        <div style={{
          background: '#13131e',
          border: '1px dashed var(--border-subtle)',
          borderRadius: 'var(--radius-md)',
          padding: '3rem 1.5rem',
          textAlign: 'center',
          color: 'var(--text-muted)'
        }}>
          <Layers size={36} color="var(--gold-400)" style={{ margin: '0 auto 0.8rem', opacity: 0.6 }} />
          <h3 style={{ color: '#fff', fontSize: '1.1rem', marginBottom: '0.4rem' }}>No Categories Found</h3>
          <p style={{ fontSize: '0.85rem', maxWidth: '400px', margin: '0 auto 1.2rem' }}>
            No category matches your search query "{searchTerm}". Clear search or create a new category.
          </p>
          <button onClick={openAddModal} className="btn btn-gold btn-sm">
            <Plus size={14} /> Add New Category
          </button>
        </div>
      )}

      {/* ADD / EDIT CATEGORY MODAL */}
      {modalOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(8px)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}>
          <div style={{
            background: '#13131e',
            border: '1px solid var(--border-gold)',
            borderRadius: 'var(--radius-lg)',
            width: '100%',
            maxWidth: '560px',
            maxHeight: '92vh',
            overflowY: 'auto',
            padding: '1.8rem',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.8)'
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1.5rem',
              borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
              paddingBottom: '0.9rem'
            }}>
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                  {editingId ? 'Edit Master Category' : 'Create New Collection Category'}
                </h3>
                <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0.2rem 0 0' }}>
                  {editingId ? `Modifying settings for slug "${editingId}"` : 'Define title, cover photo, and storefront display settings'}
                </p>
              </div>

              <button
                onClick={() => setModalOpen(false)}
                className="btn-icon"
                style={{ width: '32px', height: '32px' }}
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
              {/* Name & Slug */}
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.35rem' }}>
                  Category Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Pure Gold Coins & Bullion"
                  value={form.name}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm(prev => ({
                      ...prev,
                      name: val,
                      // Auto-update slug if in create mode and slug was matching
                      id: editingId ? prev.id : (prev.id === '' || prev.id === prev.name.toLowerCase().replace(/[^a-z0-9_-]/g, '-') ? val.toLowerCase().replace(/[^a-z0-9_-]/g, '-') : prev.id)
                    }));
                  }}
                  className="input-luxury"
                />
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)' }}>
                    Category Slug / Identifier *
                  </label>
                  <span style={{ fontSize: '0.7rem', color: 'var(--gold-400)' }}>
                    Used internally for products (e.g. "gold-coins")
                  </span>
                </div>
                <input
                  type="text"
                  required
                  disabled={Boolean(editingId)}
                  placeholder="e.g. gold-coins"
                  value={form.id}
                  onChange={(e) => setForm({ ...form, id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '-') })}
                  className="input-luxury"
                  style={{
                    fontFamily: 'monospace',
                    opacity: editingId ? 0.7 : 1,
                    cursor: editingId ? 'not-allowed' : 'text'
                  }}
                />
                {editingId && (
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                    Slug cannot be altered after creation to protect existing linked products.
                  </div>
                )}
              </div>

              {/* Subtitle / Tagline */}
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '0.35rem' }}>
                  Subtitle / Craft Tagline
                </label>
                <input
                  type="text"
                  placeholder="e.g. 24K 999 Purity Hallmarked Gold Bars & Sovereign Coins"
                  value={form.subtitle}
                  onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                  className="input-luxury"
                />
              </div>

              {/* Badge Tag */}
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '0.35rem' }}>
                  Badge Tag (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. 24K PURE, BESTSELLER, NEW, IGI CERTIFIED"
                  value={form.badge}
                  onChange={(e) => setForm({ ...form, badge: e.target.value.toUpperCase() })}
                  className="input-luxury"
                />
              </div>

              {/* Cover Image URL & File Upload */}
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '0.35rem' }}>
                  Cover Banner Image
                </label>
                <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.6rem' }}>
                  <input
                    type="url"
                    placeholder="https://images.unsplash.com/..."
                    value={form.image}
                    onChange={(e) => setForm({ ...form, image: e.target.value })}
                    className="input-luxury"
                    style={{ flex: 1 }}
                  />
                  <label
                    className="btn btn-glass"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      cursor: 'pointer',
                      fontSize: '0.78rem',
                      padding: '0.5rem 0.8rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Upload size={13} /> Browse
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>

                {/* Image Live Preview */}
                {form.image && (
                  <div style={{
                    position: 'relative',
                    height: '110px',
                    borderRadius: 'var(--radius-sm)',
                    overflow: 'hidden',
                    border: '1px solid var(--border-subtle)'
                  }}>
                    <img
                      src={form.image}
                      alt="Category Preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=800&q=80';
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      bottom: '6px',
                      left: '8px',
                      background: 'rgba(0,0,0,0.7)',
                      fontSize: '0.68rem',
                      color: 'var(--gold-300)',
                      padding: '0.15rem 0.45rem',
                      borderRadius: '3px'
                    }}>
                      Cover Photo Preview
                    </div>
                  </div>
                )}
              </div>

              {/* Homepage Grid Feature Toggle */}
              <div style={{
                background: '#0d0d16',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                padding: '0.85rem 1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer'
              }} onClick={() => setForm(p => ({ ...p, isFeatured: !p.isFeatured }))}>
                <div>
                  <div style={{ fontSize: '0.84rem', fontWeight: 600, color: '#fff' }}>
                    Feature in "Explore By Master Categories"
                  </div>
                  <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>
                    Display as a prominent luxury visual card on the storefront homepage
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={form.isFeatured !== false}
                  onChange={(e) => setForm({ ...form, isFeatured: e.target.checked })}
                  style={{ width: '18px', height: '18px', accentColor: 'var(--gold-400)', cursor: 'pointer' }}
                />
              </div>

              {/* Buttons */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '0.8rem',
                marginTop: '1rem',
                borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                paddingTop: '1.2rem'
              }}>
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="btn btn-glass"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-gold"
                  style={{ fontWeight: 700 }}
                >
                  {editingId ? 'Save Changes' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {deleteConfirmCat && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(8px)',
          zIndex: 1050,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}>
          <div style={{
            background: '#13131e',
            border: '1px solid rgba(239, 68, 68, 0.4)',
            borderRadius: 'var(--radius-lg)',
            width: '100%',
            maxWidth: '460px',
            padding: '1.6rem',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.8)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', color: '#f87171', marginBottom: '0.8rem' }}>
              <AlertTriangle size={24} />
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, margin: 0 }}>
                Delete Master Category?
              </h3>
            </div>

            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '1rem' }}>
              Are you sure you want to remove <strong>"{deleteConfirmCat.name}"</strong> (slug: <code>{deleteConfirmCat.id}</code>)?
            </p>

            {products.filter(p => p.category === deleteConfirmCat.id).length > 0 && (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: 'var(--radius-sm)',
                padding: '0.8rem',
                fontSize: '0.78rem',
                color: '#fca5a5',
                marginBottom: '1.2rem'
              }}>
                <strong>Warning:</strong> There are currently <strong>{products.filter(p => p.category === deleteConfirmCat.id).length}</strong> product(s) linked to this category. They will remain in catalog but won't match this category filter until updated.
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.8rem' }}>
              <button
                onClick={() => setDeleteConfirmCat(null)}
                className="btn btn-glass"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  deleteCategory(deleteConfirmCat.id);
                  setDeleteConfirmCat(null);
                }}
                className="btn btn-sm"
                style={{
                  background: '#ef4444',
                  color: '#fff',
                  border: 'none',
                  fontWeight: 700,
                  padding: '0.5rem 1.1rem',
                  borderRadius: 'var(--radius-sm)'
                }}
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
